import { useCallback, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { PageLoader } from '@/components/layout/PageLoader';
import { env } from '@/lib/env';

interface LocationState {
  from?: { pathname?: string };
}

/**
 * Login — full-bleed marketing page. Uses Auth0 Universal Login for
 * email/password and Google. After the Auth0 redirect completes,
 * `Auth0ProviderWithNavigate.onRedirectCallback` fires and sends the
 * user to `appState.returnTo` (or `/library` by default).
 *
 * If the user is already authenticated (e.g. navigated back to /login),
 * we bounce them straight to /library.
 *
 * Marketing-site deep link: when the URL contains `?action=signup` (or
 * `?action=login`) we skip the form entirely and auto-fire
 * `loginWithRedirect` with the matching `screen_hint`. This is what the
 * "Try the app free" CTAs on `website/` link to — the SPA SDK still
 * owns the OAuth/PKCE handshake, so the marketing site never has to
 * know the Auth0 tenant or client id.
 */
export function LoginPage() {
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoTriggeredRef = useRef(false);

  const auth0Missing = !env.isAuth0Configured;
  const returnTo = (location.state as LocationState | null)?.from?.pathname ?? '/library';

  const handleLogin = useCallback(
    (extra: Record<string, string> = {}) =>
      loginWithRedirect({
        appState: { returnTo },
        authorizationParams: {
          redirect_uri: window.location.origin,
          audience: env.auth0Audience,
          ...extra,
        },
      }),
    [loginWithRedirect, returnTo],
  );

  // Already-authenticated users skip the form entirely.
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const from = (location.state as LocationState | null)?.from?.pathname;
      navigate(from ?? '/library', { replace: true });
    }
  }, [isLoading, isAuthenticated, location.state, navigate]);

  // Marketing-site deep link auto-trigger. `?action=signup` boots the
  // Auth0 page in create-account mode; `?action=login` skips straight
  // to the sign-in form. Single-fire via ref so React StrictMode's
  // double-mount in dev doesn't kick off two redirects.
  const action = searchParams.get('action');
  const willAutoTrigger = !auth0Missing && (action === 'signup' || action === 'login');

  useEffect(() => {
    if (autoTriggeredRef.current) return;
    if (isLoading || isAuthenticated || auth0Missing) return;
    if (action !== 'signup' && action !== 'login') return;

    autoTriggeredRef.current = true;

    // Strip the param so a back-button hop after Auth0 doesn't reopen
    // the same redirect loop.
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });

    void handleLogin(action === 'signup' ? { screen_hint: 'signup' } : {});
  }, [
    isLoading,
    isAuthenticated,
    auth0Missing,
    action,
    searchParams,
    setSearchParams,
    handleLogin,
  ]);

  // Keep the loader visible from the moment we know we're going to
  // bounce to Auth0 — the form itself never paints in the deep-link
  // case, so users see one continuous "Taking you to OllieReads…" spinner
  // instead of a flash of the marketing card.
  if (isLoading || willAutoTrigger || autoTriggeredRef.current) {
    return <PageLoader label="Taking you to OllieReads…" />;
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center bg-or-cream-50 bg-cover bg-center px-6 py-12"
      style={{ backgroundImage: 'url(/brand/books_login_bg.png)' }}
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-white/40 via-or-cream-50/60 to-white/70"
      />

      <div className="relative w-full max-w-md rounded-[28px] border border-white/60 bg-white/90 p-8 shadow-or-pop backdrop-blur-sm">
        <div className="flex justify-center">
          <img
            src="/brand/olliereads_logo.png"
            alt="OllieReads"
            width={220}
            height={64}
            className="h-auto"
          />
        </div>
        <h1 className="mt-6 text-center font-display text-[30px] font-bold text-or-slate-900">
          Welcome!
        </h1>
        <p className="mt-2 text-center text-[14px] text-or-slate-600">
          Discover a world of reading, learning, and activities kids will love.
        </p>

        {auth0Missing ? (
          <div className="mt-6 rounded-2xl border border-or-rose-100 bg-or-rose-100/40 p-4 text-[12px] text-or-rose-700">
            Auth0 isn't configured yet. Set <code>VITE_AUTH0_DOMAIN</code>,{' '}
            <code>VITE_AUTH0_CLIENT_ID</code> and <code>VITE_AUTH0_AUDIENCE</code> in{' '}
            <code>client/.env</code>.
          </div>
        ) : null}

        <div className="mt-8 flex flex-col gap-3">
          <Button size="lg" onClick={() => handleLogin()} disabled={auth0Missing}>
            Sign in
          </Button>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => handleLogin({ screen_hint: 'signup' })}
            disabled={auth0Missing}
          >
            Create an account
          </Button>
          <Button
            size="lg"
            variant="ghost"
            onClick={() => handleLogin({ connection: env.auth0GoogleConnection })}
            disabled={auth0Missing}
          >
            Continue with Google
          </Button>
        </div>

        <p className="mt-6 text-center text-[11px] text-or-slate-500">
          By continuing you agree to OllieReads's terms of service.
        </p>
      </div>
    </div>
  );
}
